import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ApiTokenScope } from "shared";

import { Spinner } from "../Common/Spinner";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { FormLabel } from "../../../common/Atoms/FormLabel";
import { TwoColFormRow } from "../Molecules/Form/TwoColFormRow";
import { useFetchConnectedAppsQuery, useRevokeConnectedAppMutation } from "../../services/api/userApi/user";

export function ConnectedApps() {
  const { data, isLoading } = useFetchConnectedAppsQuery();
  const [revokeConnectedApp, { isLoading: isRevoking }] = useRevokeConnectedAppMutation();

  const apps = data?.apps ?? [];

  async function handleRevoke(tokenId: string) {
    try {
      await revokeConnectedApp(tokenId).unwrap();
      toast.success(`Connected app revoked`);
    }
    catch {
      toast.error(`Could not revoke connected app`);
    }
  }

  return (
    <TwoColFormRow
      heading={ `Connected apps` }
      description={ `AI agents and other apps you connected to Paidnice through MCP. Each can reach the organisations listed.` }
    >
      <div className={ `space-y-3` }>
        { isLoading ? (
          <Spinner />
        ) : apps.length === 0 ? (
          <Paragraph variant={ `secondary` }>
            { `No connected apps.` }
          </Paragraph>
        ) : (
          <div>
            <div className={ `grid grid-cols-12 border-b border-gray-200 pb-1` }>
              <FormLabel className={ `col-span-3` }>{ `App` }</FormLabel>
              <FormLabel className={ `col-span-3` }>{ `Organisations` }</FormLabel>
              <FormLabel className={ `col-span-2` }>{ `Access` }</FormLabel>
              <FormLabel className={ `col-span-3` }>{ `Connected` }</FormLabel>
            </div>
            { apps.map(app => (
              <div
                key={ app.id }
                className={ `grid grid-cols-12 items-center border-b border-gray-100 py-2` }
              >
                <Paragraph
                  className={ `col-span-3` }
                  variant={ `secondary` }
                >
                  { app.clientName || `Unknown app` }
                </Paragraph>
                <Paragraph
                  className={ `col-span-3` }
                  variant={ `secondary` }
                >
                  { app.organisations.map(org => org.name).join(`, `) }
                </Paragraph>
                <Paragraph
                  className={ `col-span-2` }
                  variant={ `secondary` }
                >
                  { app.scopes.includes(ApiTokenScope.WRITE) ? `Read and write` : `Read only` }
                </Paragraph>
                <Paragraph
                  className={ `col-span-3` }
                  variant={ `secondary` }
                >
                  { DateTime.fromISO(app.createdAt).toFormat(`yyyy-LL-dd`) }
                </Paragraph>
                <div className={ `col-span-1 flex justify-end` }>
                  <TrashIcon
                    className={ `h-5 w-5 text-paidniceRed-600 ${isRevoking ? `cursor-not-allowed` : `cursor-pointer`}` }
                    onClick={ () => !isRevoking && handleRevoke(app.id) }
                  />
                </div>
              </div>
            )) }
          </div>
        ) }
      </div>
    </TwoColFormRow>
  );
}
